import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Send, Phone, User, Clock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useMessages } from "@/hooks/useMessages";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ContactDetailSidebar } from "@/components/ContactDetailSidebar";
import { TemplateSelector } from "@/components/TemplateSelector";
import type { Tables } from "@/integrations/supabase/types";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import { HELP_CONTENT } from "@/lib/help-content";

interface ConversationViewProps {
  conversationId: string;
}

type Conversation = Tables<"conversations">;

const ConversationView = ({ conversationId }: ConversationViewProps) => {
  const [message, setMessage] = useState("");
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { messages, loading } = useMessages(conversationId);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchConversation = async () => {
      const { data } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      setConversation(data);
    };

    fetchConversation();
  }, [conversationId]);

  const handleTakeOver = async () => {
    try {
      const { error } = await supabase.functions.invoke('assign-conversation', {
        body: { conversation_id: conversationId }
      });

      if (error) throw error;

      toast({
        title: "Conversation assigned",
        description: "You've taken over this conversation"
      });
    } catch (error) {
      console.error('Error taking over conversation:', error);
      toast({
        title: "Error",
        description: "Failed to take over conversation",
        variant: "destructive"
      });
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    setIsLoading(true);
    try {
      const { error } = await supabase.functions.invoke('send-sms', {
        body: {
          conversation_id: conversationId,
          content: message
        }
      });

      if (error) throw error;

      setMessage("");
      toast({
        title: "Message sent",
        description: "Your message has been sent"
      });
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Group consecutive messages by sender
  const groupMessagesBySender = (msgs: any[]) => {
    const groups: Array<{
      sender_type: string;
      messages: any[];
    }> = [];
    let currentGroup: typeof groups[0] | null = null;

    msgs.forEach((msg) => {
      if (!currentGroup || currentGroup.sender_type !== msg.sender_type) {
        // Start a new group
        currentGroup = {
          sender_type: msg.sender_type,
          messages: [msg]
        };
        groups.push(currentGroup);
      } else {
        // Add to existing group
        currentGroup.messages.push(msg);
      }
    });

    return groups;
  };

  const messageGroups = groupMessagesBySender(messages);

  return (
    <div className="grid grid-cols-3 gap-4">
      <Card className="col-span-2 h-[600px] flex flex-col border-0 shadow-[var(--shadow-soft)] overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <User className="h-5 w-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="font-semibold">
                {conversation?.customer_name || conversation?.customer_phone || "Loading..."}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-3 w-3" />
                {conversation?.customer_phone}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {conversation?.assigned_agent_id && (
              <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                Agent-Initiated
              </Badge>
            )}
            <Badge variant="outline" className={cn("text-xs", 
              conversation?.status === 'active' && "bg-accent/10 text-accent border-accent/20",
              conversation?.status === 'waiting' && "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
              conversation?.status === 'closed' && "bg-muted text-muted-foreground border-border"
            )}>
              <div className="h-2 w-2 rounded-full bg-current mr-1.5 animate-pulse-soft" />
              {conversation?.status}
            </Badge>
            {conversation?.crm_contact_id && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/dashboard?tab=crm')}
                className="gap-2"
              >
                <ExternalLink className="h-3 w-3" />
                View in CRM
              </Button>
            )}
            {conversation?.status === 'waiting' && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={handleTakeOver}>
                  Take Over
                </Button>
                <HelpTooltip {...HELP_CONTENT.conversation.takeOver} side="left" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center text-muted-foreground">Loading messages...</div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted-foreground">No messages yet</div>
        ) : (
          messageGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="flex flex-col gap-1">
              {group.messages.map((msg, msgIndex) => {
                const isFirst = msgIndex === 0;
                const isLast = msgIndex === group.messages.length - 1;
                const isSingle = group.messages.length === 1;
                const isAgent = group.sender_type === "agent";
                
                return (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex animate-slide-up",
                      isAgent ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[70%] p-3",
                        // Dynamic border radius
                        isSingle && "rounded-lg",
                        !isSingle && isFirst && (isAgent ? "rounded-t-lg rounded-bl-lg" : "rounded-t-lg rounded-br-lg"),
                        !isSingle && !isFirst && !isLast && (isAgent ? "rounded-l-lg" : "rounded-r-lg"),
                        !isSingle && isLast && (isAgent ? "rounded-b-lg rounded-tl-lg" : "rounded-b-lg rounded-tr-lg"),
                        // Background colors
                        msg.sender_type === "agent"
                          ? "bg-primary text-primary-foreground"
                          : msg.sender_type === "ai"
                          ? "bg-accent/20 border border-accent/30"
                          : "bg-muted"
                      )}
                    >
                      {isFirst && (
                        <div className="flex items-center gap-2 mb-1">
                          {msg.sender_type === "ai" && (
                            <Badge variant="outline" className="text-xs">AI</Badge>
                          )}
                          {msg.sentiment && (
                            <Badge variant="outline" className={cn("text-xs",
                              msg.sentiment === 'positive' && "bg-green-500/10 text-green-600 border-green-500/20",
                              msg.sentiment === 'neutral' && "bg-gray-500/10 text-gray-600 border-gray-500/20",
                              msg.sentiment === 'negative' && "bg-red-500/10 text-red-600 border-red-500/20",
                              msg.sentiment === 'urgent' && "bg-orange-500/10 text-orange-600 border-orange-500/20"
                            )}>
                              {msg.sentiment === 'positive' && '😊'}
                              {msg.sentiment === 'neutral' && '😐'}
                              {msg.sentiment === 'negative' && '😟'}
                              {msg.sentiment === 'urgent' && '🚨'}
                              {msg.sentiment}
                            </Badge>
                          )}
                        </div>
                      )}
                      <p className="text-sm">{msg.content}</p>
                      {isLast && (
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="h-3 w-3 opacity-70" />
                          <span className="text-xs opacity-70">
                            {format(new Date(msg.created_at), 'p')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Input */}
      <div className="p-4 border-t bg-muted/30">
        {conversation?.status === 'closed' && (
          <div className="mb-3 p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">Was this conversation resolved successfully?</p>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  const lastAgentMsg = messages.filter(m => m.sender_type === 'agent').pop();
                  const lastCustomerMsg = messages.filter(m => m.sender_type === 'customer').pop();
                  
                  if (lastAgentMsg && lastCustomerMsg) {
                    await supabase.from('ai_training_data').insert({
                      conversation_id: conversationId,
                      customer_message: lastCustomerMsg.content,
                      agent_response: lastAgentMsg.content,
                      was_helpful: true,
                      category: 'FAQ'
                    });
                    
                    toast({
                      title: "Thank you!",
                      description: "AI will learn from this conversation",
                    });
                  }
                }}
              >
                ✓ Yes, helpful
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                onClick={async () => {
                  const lastAgentMsg = messages.filter(m => m.sender_type === 'agent').pop();
                  const lastCustomerMsg = messages.filter(m => m.sender_type === 'customer').pop();
                  
                  if (lastAgentMsg && lastCustomerMsg) {
                    await supabase.from('ai_training_data').insert({
                      conversation_id: conversationId,
                      customer_message: lastCustomerMsg.content,
                      agent_response: lastAgentMsg.content,
                      was_helpful: false
                    });
                    
                    toast({
                      title: "Noted",
                      description: "Feedback recorded",
                    });
                  }
                }}
              >
                ✗ Needs improvement
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <TemplateSelector onSelectTemplate={(content) => setMessage(content)} />
          <Textarea
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            className="min-h-[80px] resize-none"
          />
          <Button onClick={handleSend} className="gap-2 self-end" disabled={isLoading}>
            <Send className="h-4 w-4" />
            {isLoading ? "Sending..." : "Send"}
          </Button>
        </div>
      </div>
    </Card>

    {/* Contact Sidebar */}
    <ContactDetailSidebar contactId={conversation?.crm_contact_id || null} />
    </div>
  );
};

export default ConversationView;
